import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Users,
  Mail,
  LinkIcon,
  Loader2,
  Trash2,
  Check,
  Copy,
  Pencil,
  X,
  ShieldCheck,
} from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { PLATFORM_META, type Platform } from '../lib/platforms';
import { AccountAvatar } from '../components/AccountAvatar';
import { TEAM_SECTIONS } from '../lib/teamSections';
import { cn } from '../lib/cn';

type Account = { id: string; platform: Platform; username: string | null; displayName: string | null; profilePhotoUrl: string | null };
type Member = { id: string; memberUserId: string; email: string; sections: string[]; allAccounts: boolean; accountIds: string[]; createdAt: string };
type Invite = { id: string; email: string | null; token: string; sections: string[]; allAccounts: boolean; accountIds: string[]; createdAt: string };
type TeamData = { seats: { limit: number | null; current: number }; members: Member[]; invites: Invite[]; accounts: Account[] };

type Perms = { sections: string[]; allAccounts: boolean; accountIds: string[] };

export function Team() {
  const qc = useQueryClient();
  const query = useQuery({ queryKey: ['team'], queryFn: () => api<TeamData>('/api/team') });
  const [inviteOpen, setInviteOpen] = useState(false);

  if (query.isLoading) {
    return <div className="mt-10 flex justify-center text-slate-400"><Loader2 className="animate-spin" size={26} /></div>;
  }

  const data = query.data!;
  const { seats } = data;
  const seatsLeft = seats.limit === null ? Infinity : Math.max(0, seats.limit - seats.current - data.invites.length);
  const seatsLabel = seats.limit === null ? 'Ilimitados' : `${seats.current + data.invites.length} / ${seats.limit}`;

  return (
    <>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Users size={22} className="text-brand-600" />
            Equipos
          </h1>
          <p className="mt-2 text-slate-600">
            Invita a tu equipo y decide a qué secciones y cuentas puede acceder cada persona.
          </p>
        </div>
        <button
          onClick={() => setInviteOpen(true)}
          disabled={seatsLeft <= 0}
          className="btn-primary gap-2"
          title={seatsLeft <= 0 ? 'Has alcanzado el límite de tu plan' : undefined}
        >
          <Mail size={14} /> Invitar
        </button>
      </div>

      <div className="mt-4 inline-flex items-center gap-2 text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5">
        <Users size={14} /> Plazas usadas: <span className="font-medium text-slate-700">{seatsLabel}</span>
      </div>

      {inviteOpen && (
        <InviteModal
          accounts={data.accounts}
          onClose={() => setInviteOpen(false)}
          onDone={() => { setInviteOpen(false); qc.invalidateQueries({ queryKey: ['team'] }); }}
        />
      )}

      {/* Members */}
      <h2 className="mt-8 text-sm font-semibold text-slate-500 uppercase tracking-wide">Miembros</h2>
      {data.members.length === 0 ? (
        <p className="mt-3 text-sm text-slate-500">Aún no hay miembros. Invita a tu primera persona.</p>
      ) : (
        <div className="mt-3 space-y-3">
          {data.members.map((m) => (
            <MemberRow key={m.id} member={m} accounts={data.accounts} qc={qc} />
          ))}
        </div>
      )}

      {/* Pending invites */}
      {data.invites.length > 0 && (
        <>
          <h2 className="mt-8 text-sm font-semibold text-slate-500 uppercase tracking-wide">Invitaciones pendientes</h2>
          <div className="mt-3 space-y-2">
            {data.invites.map((inv) => (
              <InviteRow key={inv.id} invite={inv} qc={qc} />
            ))}
          </div>
        </>
      )}
    </>
  );
}

/* ── Permissions editor (shared by invite + member edit) ──── */
function PermsEditor({ accounts, value, onChange }: { accounts: Account[]; value: Perms; onChange: (p: Perms) => void }) {
  const toggleSection = (key: string) => {
    const has = value.sections.includes(key);
    onChange({ ...value, sections: has ? value.sections.filter((s) => s !== key) : [...value.sections, key] });
  };
  const toggleAccount = (id: string) => {
    const has = value.accountIds.includes(id);
    onChange({ ...value, accountIds: has ? value.accountIds.filter((a) => a !== id) : [...value.accountIds, id] });
  };
  return (
    <div className="space-y-4">
      <div>
        <div className="label">Secciones a las que puede acceder</div>
        <div className="flex flex-wrap gap-1.5">
          {TEAM_SECTIONS.map((s) => {
            const on = value.sections.includes(s.key);
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => toggleSection(s.key)}
                className={cn(
                  'text-xs px-2.5 py-1.5 rounded-lg border transition',
                  on ? 'bg-brand-600 text-white border-brand-600' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300',
                )}
              >
                {on && <Check size={11} className="inline mr-1 -mt-0.5" />}{s.label}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <div className="label">Cuentas a las que puede acceder</div>
        <label className="flex items-center gap-2 text-sm text-slate-700 mb-2">
          <input type="checkbox" checked={value.allAccounts} onChange={(e) => onChange({ ...value, allAccounts: e.target.checked })} className="accent-brand-600" />
          Todas las cuentas
        </label>
        {!value.allAccounts && (
          <div className="flex flex-wrap gap-1.5">
            {accounts.length === 0 && <span className="text-xs text-slate-400">No tienes cuentas conectadas.</span>}
            {accounts.map((a) => {
              const on = value.accountIds.includes(a.id);
              return (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => toggleAccount(a.id)}
                  className={cn(
                    'inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-lg border transition',
                    on ? 'border-brand-300 bg-brand-50 text-brand-700' : 'border-slate-200 text-slate-600 hover:border-slate-300',
                  )}
                >
                  <AccountAvatar account={a} size={18} />
                  {a.username ?? a.displayName ?? PLATFORM_META[a.platform].label}
                  {on && <Check size={11} />}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function InviteModal({ accounts, onClose, onDone }: { accounts: Account[]; onClose: () => void; onDone: () => void }) {
  const [email, setEmail] = useState('');
  const [perms, setPerms] = useState<Perms>({ sections: TEAM_SECTIONS.map((s) => s.key), allAccounts: true, accountIds: [] });
  const [linkUrl, setLinkUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const sendEmail = useMutation({
    mutationFn: () => api<{ invite: { joinUrl: string } }>('/api/team/invite', { method: 'POST', body: JSON.stringify({ email: email.trim(), ...perms }) }),
    onSuccess: () => onDone(),
  });
  const makeLink = useMutation({
    mutationFn: () => api<{ invite: { joinUrl: string } }>('/api/team/invite-link', { method: 'POST', body: JSON.stringify(perms) }),
    onSuccess: (d) => setLinkUrl(d.invite.joinUrl),
  });

  const emailValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());
  const errMsg = (e: unknown) =>
    e instanceof ApiError && e.code === 'LIMIT_REACHED' ? 'Has alcanzado el límite de miembros de tu plan.'
    : e instanceof ApiError && e.code === 'FORBIDDEN' ? 'Tu plan no incluye equipos.'
    : 'No se pudo crear la invitación.';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="text-lg font-semibold text-slate-900">Invitar a tu equipo</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
        </div>

        <div className="px-6 py-4 space-y-4">
          <PermsEditor accounts={accounts} value={perms} onChange={setPerms} />

          <div className="border-t border-slate-100 pt-4 space-y-3">
            <div>
              <label className="label">Invitar por email</label>
              <div className="flex items-center gap-2">
                <input className="input flex-1" type="email" placeholder="email@persona.com" value={email} onChange={(e) => setEmail(e.target.value)} />
                <button onClick={() => sendEmail.mutate()} disabled={!emailValid || sendEmail.isPending} className="btn-primary gap-2 shrink-0">
                  {sendEmail.isPending ? <Loader2 className="animate-spin" size={14} /> : <Mail size={14} />} Enviar
                </button>
              </div>
              {sendEmail.isError && <p className="mt-1 text-xs text-red-600">{errMsg(sendEmail.error)}</p>}
            </div>

            <div>
              <label className="label">…o comparte un enlace</label>
              {linkUrl ? (
                <div className="flex items-center gap-2">
                  <input readOnly value={linkUrl} onFocus={(e) => e.currentTarget.select()} className="input flex-1 text-sm font-mono" />
                  <button
                    onClick={async () => { try { await navigator.clipboard.writeText(linkUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch {} }}
                    className="btn-secondary gap-2 shrink-0"
                  >
                    {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? 'Copiado' : 'Copiar'}
                  </button>
                </div>
              ) : (
                <button onClick={() => makeLink.mutate()} disabled={makeLink.isPending} className="btn-secondary gap-2">
                  {makeLink.isPending ? <Loader2 className="animate-spin" size={14} /> : <LinkIcon size={14} />} Generar enlace de invitación
                </button>
              )}
              {makeLink.isError && <p className="mt-1 text-xs text-red-600">{errMsg(makeLink.error)}</p>}
              <p className="mt-1.5 text-[11px] text-slate-400">El enlace usa los permisos seleccionados arriba. Cada invitación es de un solo uso.</p>
            </div>
          </div>
        </div>

        <div className="px-6 py-3 border-t border-slate-100 text-right">
          <button onClick={onClose} className="btn-secondary">{linkUrl ? 'Listo' : 'Cancelar'}</button>
        </div>
      </div>
    </div>
  );
}

function MemberRow({ member, accounts, qc }: { member: Member; accounts: Account[]; qc: ReturnType<typeof useQueryClient> }) {
  const [editing, setEditing] = useState(false);
  const [perms, setPerms] = useState<Perms>({ sections: member.sections, allAccounts: member.allAccounts, accountIds: member.accountIds });
  const [confirmRemove, setConfirmRemove] = useState(false);

  const save = useMutation({
    mutationFn: () => api(`/api/team/members/${member.id}`, { method: 'PATCH', body: JSON.stringify(perms) }),
    onSuccess: () => { setEditing(false); qc.invalidateQueries({ queryKey: ['team'] }); },
  });
  const remove = useMutation({
    mutationFn: () => api(`/api/team/members/${member.id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team'] }),
  });

  const sectionLabels = member.sections.length
    ? TEAM_SECTIONS.filter((s) => member.sections.includes(s.key)).map((s) => s.label).join(', ')
    : 'Sin secciones';

  return (
    <div className="card">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="h-9 w-9 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center font-semibold">
          {member.email.charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-slate-900 truncate">{member.email}</div>
          <div className="text-xs text-slate-500">
            {sectionLabels} · {member.allAccounts ? 'Todas las cuentas' : `${member.accountIds.length} cuenta(s)`}
          </div>
        </div>
        {!editing && (
          <button onClick={() => setEditing(true)} className="btn-secondary gap-1.5 text-sm"><Pencil size={13} /> Permisos</button>
        )}
        {confirmRemove ? (
          <span className="inline-flex items-center gap-2 text-sm">
            <span className="text-slate-500">¿Quitar?</span>
            <button onClick={() => remove.mutate()} disabled={remove.isPending} className="text-red-600 font-medium">Sí</button>
            <button onClick={() => setConfirmRemove(false)} className="text-slate-500">No</button>
          </span>
        ) : (
          <button onClick={() => setConfirmRemove(true)} className="text-slate-400 hover:text-red-600"><Trash2 size={15} /></button>
        )}
      </div>

      {editing && (
        <div className="mt-4 border-t border-slate-100 pt-4">
          <PermsEditor accounts={accounts} value={perms} onChange={setPerms} />
          <div className="mt-4 flex justify-end gap-2">
            <button onClick={() => { setEditing(false); setPerms({ sections: member.sections, allAccounts: member.allAccounts, accountIds: member.accountIds }); }} className="btn-secondary">Cancelar</button>
            <button onClick={() => save.mutate()} disabled={save.isPending} className="btn-primary gap-2">
              {save.isPending ? <Loader2 className="animate-spin" size={14} /> : <Check size={14} />} Guardar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function InviteRow({ invite, qc }: { invite: Invite; qc: ReturnType<typeof useQueryClient> }) {
  const [copied, setCopied] = useState(false);
  const joinUrl = `${window.location.origin}/join/${invite.token}`;
  const revoke = useMutation({
    mutationFn: () => api(`/api/team/invites/${invite.id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team'] }),
  });
  return (
    <div className="flex items-center gap-3 rounded-xl border border-slate-200 p-3 flex-wrap">
      <Mail size={16} className="text-slate-400 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-sm text-slate-800 truncate">{invite.email ?? 'Invitación por enlace'}</div>
        <div className="text-xs text-amber-600">Pendiente</div>
      </div>
      <button
        onClick={async () => { try { await navigator.clipboard.writeText(joinUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch {} }}
        className="btn-secondary gap-1.5 text-sm"
      >
        {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? 'Copiado' : 'Copiar enlace'}
      </button>
      <button onClick={() => revoke.mutate()} disabled={revoke.isPending} className="text-slate-400 hover:text-red-600"><Trash2 size={15} /></button>
    </div>
  );
}
